/** Mirrors the Worker's Clockify-style error envelope. */
export interface ErrorEnvelope {
  message: string;
  code: number;
  /** Clone-only stable upper-snake condition (architecture 4), e.g. `RATE_LIMITED`. */
  reason?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: number;
  readonly reason: string | null;

  constructor(status: number, envelope: ErrorEnvelope) {
    super(envelope.message);
    this.name = "ApiError";
    this.status = status;
    this.code = envelope.code;
    this.reason = typeof envelope.reason === "string" ? envelope.reason : null;
  }
}

export interface ApiRequestInit extends Omit<RequestInit, "body"> {
  body?: unknown;
}

export async function api<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  const method = (init.method ?? "GET").toUpperCase();
  // The Worker's CSRF check rejects cookie-authenticated mutations that lack this header,
  // which a cross-site form post cannot set.
  if (method !== "GET" && method !== "HEAD") headers.set("X-Requested-With", "XMLHttpRequest");
  let body: BodyInit | undefined;
  if (init.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.body);
  }

  const res = await fetch(path, { ...init, headers, body, credentials: "same-origin" });
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    // A proxy or the SPA fallback can answer with HTML; surface that as an ApiError rather than a SyntaxError.
    data = undefined;
  }
  if (!res.ok) {
    const envelope =
      data && typeof data === "object" && "message" in data
        ? (data as ErrorEnvelope)
        : { message: res.statusText || "Request failed", code: res.status };
    throw new ApiError(res.status, envelope);
  }
  return data as T;
}
